/**
 * editor - WYSIWYG rich-text fields over a hidden <textarea>, entry point.
 *
 * The textarea IS the field: it keeps its `name`, its `validate-*` rules and
 * its authored default content; the library hides it inside a `.gedit`
 * wrapper, renders the visual editor next to it and keeps `textarea.value`
 * in sync as CLEAN HTML ('' while the content is really empty). Native
 * `input`/`change` events mirror a plain textarea, so validator and former
 * treat an editor field like any other (validation rules run on the plain
 * TEXT via the validator's .gedit branch).
 *
 * Usage:
 *   <textarea name="body" editor editor-toolbar="full" validate-required></textarea>
 *   g.editor('#body', { toolbar: 'basic' });    // programmatic (same instance: el.editor)
 *   g.editor.get('#body');                      // instance (also: el.editor)
 *
 * Options resolution (later wins): g.editor.defaults < init options <
 * `editor-*` element attributes (kebab->camelCase, "true"/"false" -> boolean,
 * numbers -> numbers, bare attribute -> true). The bare `editor` attribute
 * value picks the flavor (`<textarea editor="quill">`; empty = default).
 *
 * Loaded PER PAGE together with its engine vendor: add
 * `'vendors' => ['quill'], 'libs' => ['editor']` (or `['tiptap']` for the
 * tiptap flavor) to the page's route.php. Full reference: docs/libs/editor.md.
 */
import { Editor } from './editor.js';
import { QuillEngine } from './engine-quill.js';
import { TiptapEngine } from './engine-tiptap.js';

// Bump on every meaningful change to the library.
const VERSION = '0.3.1';

// flavor -> { engine class, vendor global to check, route vendors name }
const FLAVORS = {
	quill: { engine: QuillEngine, global: 'Quill', vendor: 'quill' },
	tiptap: { engine: TiptapEngine, global: 'TipTap', vendor: 'tiptap' },
};

const defaults = {
	flavor: 'quill',   // 'quill' | 'tiptap' (docs/libs/editor.md "Engines")
	toolbar: 'basic',  // 'basic' | 'full' (docs/libs/editor.md)
	height: 0,         // content-area min-height in px; 0 = the stylesheet default
	placeholder: '',   // '' = the textarea's placeholder attribute
};

const camel = (s) => s.replace(/-([a-z])/g, (m, c) => c.toUpperCase());

const coerce = (v) => {
	if (v === '') return true;
	if (v === 'true') return true;
	if (v === 'false') return false;
	if (v !== '' && !isNaN(v) && String(Number(v)) === v) return Number(v);
	return v;
};

function attrOptions(el) {
	const opts = {};
	for (const attr of el.attributes) {
		let name = null;
		if (attr.name.startsWith('data-editor-')) name = attr.name.slice(12);
		else if (attr.name.startsWith('editor-')) name = attr.name.slice(7);
		if (!name) continue;
		opts[camel(name)] = coerce(attr.value);
	}
	const flavor = el.getAttribute('editor') || el.getAttribute('data-editor');
	if (flavor) opts.flavor = flavor;
	return opts;
}

function editor(target, options) {
	let els;
	if (typeof target === 'string') els = Array.from(document.querySelectorAll(target));
	else if (target instanceof Element) els = [target];
	else els = Array.from(target || []);

	const instances = els.map((el) => {
		if (el.editor) return el.editor;
		const opts = { ...defaults, ...options, ...attrOptions(el) };
		const flavor = FLAVORS[opts.flavor];
		if (!flavor) {
			console.error('[editor] unknown flavor "' + opts.flavor + '" - available: ' + Object.keys(FLAVORS).join(', '));
			return null;
		}
		if (typeof window[flavor.global] === 'undefined') {
			console.error("[editor] " + flavor.global + " vendor not loaded on this page - add 'vendors' => ['" + flavor.vendor + "'] in the page's route.php");
			return null;
		}
		return new Editor(el, opts, flavor.engine);
	}).filter(Boolean);
	return instances.length === 1 ? instances[0] : instances;
}

editor.version = () => VERSION;
editor.defaults = defaults;
editor.get = (target) => {
	const el = typeof target === 'string' ? document.querySelector(target) : target;
	return (el && el.editor) || null;
};

function autoInit() {
	editor('textarea[editor], textarea[data-editor]');
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', autoInit);
else autoInit();

if (window.g) {
	window.g.editor = editor;
	window.g.registerLib('editor');
} else {
	window.editor = editor;
}
